import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Grid, Paper, Stack, Divider, 
  LinearProgress, CircularProgress, IconButton, Tooltip, Alert,
  Avatar, List, ListItem, ListItemText, ListItemAvatar, Chip
} from '@mui/material';
import { 
  Business, PhonelinkSetup, Refresh, ErrorOutline,
  CheckCircle, PendingActions, TrendingUp, WarningAmber,
  Https // Corrected icon source
} from '@mui/icons-material';
import { VersionDistributionChart } from './VersionDistributionChart';
import { SSLMonitor } from './SSLMonitor';

// --- SUB-COMPONENT: BIG METRIC CARD ---
const MetricCard = ({ title, value, subtitle, icon, color }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #E2E8F0', height: '100%', bgcolor: '#fff' }}>
    <Stack direction="row" justifyContent="space-between">
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#64748B', letterSpacing: 1 }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 900, color: '#1A3A3F', my: 1 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: color }}>
          {subtitle}
        </Typography>
      </Box>
      <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 48, height: 48, borderRadius: 1.5 }}>
        {icon}
      </Avatar>
    </Stack>
  </Paper>
);

export const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sslList, setSslList] = useState([]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token'); // Using your established key
      const headers = { 'Authorization': `Bearer ${token}` };

      // FETCH BOTH ENDPOINTS
      const [statsRes, sslRes] = await Promise.all([
        fetch('https://console.dreamtec.io/dashboard/global-stats', { headers }),
        fetch('https://console.dreamtec.io/customers/ssl/all-expirations', { headers })
      ]);

      if (!statsRes.ok || !sslRes.ok) throw new Error('Failed to fetch telemetry from server');

      const statsData = await statsRes.json();
      const sslData = await sslRes.json();

      setData(statsData);
      setSslList(sslData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <CircularProgress sx={{ color: '#2F727C', mb: 2 }} />
      <Typography sx={{ fontWeight: 800, color: '#94a3b8' }}>AGGREGATING GLOBAL TELEMETRY...</Typography>
    </Box>
  );

  if (error) return <Alert severity="error" sx={{ borderRadius: 2, mt: 4 }}>{error}</Alert>;

  const connectivityRate = data ? Math.round((data.fleet.online_devices / data.fleet.total_devices) * 100) : 0;

  return (
    <Box sx={{ p: 1 }}>
      {/* ─── HEADER ─── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900, color: '#1A3A3F' }}>Overview</Typography>
          <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 600 }}>Global Operations & Update Monitoring</Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={fetchStats} sx={{ bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: 2 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Grid container spacing={3}>
        {/* ─── ROW 1: PRIMARY KPIs (Corrected md={3} for 4-column row) ─── */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="MANAGED ENTITIES" value={data.total_entities} subtitle="Global Registry" icon={<Business />} color="#2F727C" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="SSL MONITORING" value={sslList.length} subtitle="Active Certificates" icon={<Https />} color="#2F727C" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="DEPLOYMENT" value={`${data.updates.success_rate}%`} subtitle="Success Verified" icon={<TrendingUp />} color="#10b981" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="TOTAL DEVICES" value={data.fleet.total_devices} subtitle={`${data.fleet.online_devices} Online Now`} icon={<PhonelinkSetup />} color="#F6B93B" />
        </Grid>

        {/* ─── ROW 2: UPDATE HEALTH & VERSION CHART ─── */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', height: '100%', bgcolor: '#fff' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
               <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1A3A3F' }}>Update Health</Typography>
              <Chip label="LAST 24H" size="small" sx={{ fontSize: '0.6rem', fontWeight: 900, height: 20, bgcolor: '#F1F5F9' }} />
            </Stack>

            <Stack spacing={2.5} sx={{ mb: 3 }}>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748B' }}>Success Rate</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 900, color: '#10b981' }}>{data.updates.success_rate}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={data.updates.success_rate} sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { bgcolor: '#10b981' } }} />
              </Box>
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748B' }}>Connectivity</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 900, color: '#2F727C' }}>{connectivityRate}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={connectivityRate} sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { bgcolor: '#2F727C' } }} />
              </Box>
            </Stack>

            <Divider sx={{ mb: 3, borderStyle: 'dashed' }} />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: 'rgba(211, 47, 47, 0.04)', borderRadius: 2, border: '1px solid rgba(211, 47, 47, 0.1)', textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#d32f2f', lineHeight: 1 }}>{data.updates.failed_count}</Typography>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#d32f2f', mt: 0.5 }}>FAILED LOGS</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: 'rgba(47, 114, 124, 0.04)', borderRadius: 2, border: '1px solid rgba(47, 114, 124, 0.1)', textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#2F727C', lineHeight: 1 }}>{data.updates.pending_count}</Typography>
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#2F727C', mt: 0.5 }}>PENDING PUSH</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <VersionDistributionChart />
        </Grid>

        {/* ─── ROW 3: INCIDENTS & SSL MONITOR ─── */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #E2E8F0', height: '100%', bgcolor: '#fff' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#1A3A3F', mb: 0.5 }}>ACTIVE INCIDENTS</Typography>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, display: 'block', mb: 2 }}>TENANTS WITH CRITICAL UPDATE FAILURES</Typography>
            
            <List disablePadding>
              {data.updates.top_incidents.length > 0 ? data.updates.top_incidents.map((incident, i) => (
                <ListItem key={i} divider={i < data.updates.top_incidents.length - 1} sx={{ py: 1.5 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#fff1f0', color: '#cf1322', width: 36, height: 36 }}>
                      <WarningAmber sx={{ fontSize: 20 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={<Typography variant="body2" sx={{ fontWeight: 800 }}>{incident.entity_name}</Typography>} 
                    secondary={`${incident.fail_count} SOTI Failures Detected`}
                    secondaryTypographyProps={{ sx: { fontSize: '0.7rem', fontWeight: 700, color: '#cf1322' } }}
                  />
                  <Chip label="VIEW LOGS" size="small" component="a" href={`/manage/fleet/devices?cid=${incident.cid}`} clickable sx={{ fontWeight: 900, fontSize: '0.6rem' }} />
                </ListItem>
              )) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircle sx={{ color: '#10b981', fontSize: 32, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>All Environments Stable</Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <SSLMonitor sslData={sslList} />
        </Grid>

        {/* ─── ALERT FOOTER ─── */}
        {data.updates.failed_count > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#fffbe6', border: '1px solid #ffe58f' }}>
              Action Required: {data.updates.failed_count} hardware updates failed. Review SOTI Profile permissions.
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};